import type { Material, Object3D } from 'three'
import { Mesh } from 'three'
import { sortBy } from 'ramda'
import type { ModelNode } from '@/entities/model3d'
import type { Hotspot } from '@/entities/site'
import type { Category } from '@/composables/useLayerSelect'
import type { Layer } from './model'
import type { TextLabel } from './labels-manager'

const DEFAULT_COLOR = 'rgba(34, 34, 34, 1)'

const _getMaterial = (sceneNode: Object3D | Mesh, nodes: Map<number, ModelNode>): Material | null => {
  const sortByLayerOrder = sortBy((node: Object3D) => nodes.get(node.userData.nodeId)?.layer ?? 1e10)
  let material = sceneNode instanceof Mesh ? sceneNode.material : null
  if (!material) {
    const children = sortByLayerOrder(sceneNode.children)
    // TODO: recurse down the node tree indefinitely as iOS does
    // So far, there doesn't seem to be a need for this, but there might be in the future.
    if (children[0] instanceof Mesh) {
      material = children[0].material
    }
  }

  return Array.isArray(material) ? material[0] : material
}

export const getLayerColour = (sceneNode: Object3D | Mesh | undefined, nodes: Map<number, ModelNode>): string => {
  if (!sceneNode) return DEFAULT_COLOR

  let color = DEFAULT_COLOR
  // set the colors for the dot on the styling if there is a valid material
  // and it can be read from the actual list.
  const material = _getMaterial(sceneNode, nodes)
  if (material) {
    if (!(material as any).color) {
      return color
    }
    let { r, g, b } = (material as any).color

    if (r !== 1 || g !== 1 || b !== 1) {
      // https://www.w3.org/TR/WCAG/#dfn-relative-luminance
      const rl = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4)
      const gl = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4)
      const bl = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4)
      const relativeLuminance = 0.2126 * rl + 0.7152 * gl + 0.0722 * bl

      // https://www.w3.org/TR/WCAG/#dfn-contrast-ratio
      // 1.05 because we assume the background is white
      const contrastRatio = 1.05 / (relativeLuminance + 0.05)

      // Invert the color if it's too close to white
      if (contrastRatio < 1.25) {
        r = 1 - r
        g = 1 - g
        b = 1 - b
      }

      color = `rgba(${[r, g, b].map(x => Math.round(x * 255)).join(', ')}, 1)`
    }
  }

  return color
}

export function isLabel(v: Layer | TextLabel | Hotspot | Category): v is TextLabel {
  return 'content' in v
}

export function isLayer(v: Layer | TextLabel | Hotspot | Category): v is Layer {
  return !('content' in v) && !('categoryName' in v) && !('site_id' in v)
}
